import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { CssBaseline, useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { DARK_SCHEME_QUERY, readStoredMode, storeMode } from './mode';
import { themes } from './theme';
import type { ThemeMode } from './tokens';

type ThemeModeContextValue = { mode: ThemeMode; toggleMode: () => void };

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

type ThemeModeProviderProps = {
  children: ReactNode;
  /** Forces a starting mode, so a test does not depend on storage or the OS. */
  initialMode?: ThemeMode;
};

/**
 * Owns the mode, the theme and the baseline.
 *
 * **The one place `ThemeProvider` and `CssBaseline` are mounted**, so the rule
 * that the first wraps the second cannot be got wrong in a second place.
 *
 * **A stored choice wins forever once made, and until then the OS decides
 * live.** `chosen` is `null` while nothing has been picked, which is when
 * `useMediaQuery` supplies the answer and keeps supplying it as the system
 * preference changes underneath.
 */
export const ThemeModeProvider = ({
  children,
  initialMode,
}: ThemeModeProviderProps): ReactElement => {
  const prefersDark = useMediaQuery(DARK_SCHEME_QUERY);
  const [chosen, setChosen] = useState<ThemeMode | null>(() => initialMode ?? readStoredMode());

  const mode = chosen ?? (prefersDark ? 'dark' : 'light');

  const toggleMode = useCallback(() => {
    const next = mode === 'dark' ? 'light' : 'dark';

    storeMode(next);
    setChosen(next);
  }, [mode]);

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeModeContext value={value}>
      <ThemeProvider theme={themes[mode]}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext>
  );
};

export const useThemeMode = (): ThemeModeContextValue => {
  const value = useContext(ThemeModeContext);

  if (value === undefined) {
    throw new Error('useThemeMode must be used inside a ThemeModeProvider');
  }

  return value;
};
